// filepath: apps/web/src/features/admin/templates/update/TemplateUpdateBadge.tsx
//
// FR-UI-ADM-12 AC-1 — notification badge for the template
// registry / templates surface.
//
// Renders the "N updates available" count when the list query
// returns >= 1 row. The badge carries three independent signals
// per FR-UI-CORE-9: an icon (⚡), translated text, and
// `data-state="update_available"` so the surface is multi-channel
// accessible.
//
// The badge is a deep-link to /admin/templates/updates. The
// surface that mounts the badge (TemplateRegistryPage) is
// responsible for the "View Update" button rendering — the badge
// itself is informational + links only.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useTemplateUpdates } from "./queries";

interface TemplateUpdateBadgeProps {
  /**
   * Optional click handler. When provided, the badge becomes a
   * button; when not provided, the badge is a span. Default
   * behavior: navigate to /admin/templates/updates.
   */
  onClick?: () => void;
}

export function TemplateUpdateBadge({
  onClick,
}: TemplateUpdateBadgeProps): React.ReactElement | null {
  const { t } = useTranslation();
  const list = useTemplateUpdates();

  if (list.isLoading || list.error || !list.data) {
    return null;
  }
  const count = list.data.updates.length;
  if (count === 0) {
    return null;
  }

  const label =
    count === 1
      ? t("admin.templates.update.badge.singular", undefined, { count })
      : t("admin.templates.update.badge.plural", undefined, { count });

  const content = (
    <>
      <span aria-hidden="true">⚡</span>
      <span>{label}</span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-900 hover:bg-blue-200"
        data-state="update_available"
        data-testid="template-update-badge"
        aria-label={t("admin.templates.update.badge.aria", undefined, {
          count,
        })}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-900"
      data-state="update_available"
      data-testid="template-update-badge"
      aria-label={t("admin.templates.update.badge.aria", undefined, { count })}
    >
      {content}
    </span>
  );
}
